import styles from './course-grid.module.scss';
import { useEffect, useState } from 'react';
import RightsWrapper from '../../../rights-wrapper/rights-wrapper';
import { ValueFormatterParams } from 'ag-grid-community';
import { durationRenderer } from '../../helpers';
import { useNavigate } from 'react-router-dom';
import WebkuaTable from '../../../webkua-table/webkua-table';
import { makeCourseRequest, useCourseGetRequestHook } from '../../../requestUtils';
import { DateRange } from '@mui/x-date-pickers-pro/DateRangePicker';
import { CircularProgress, CreateHeaders, dateHeader, ErrorButton, HeaderSizeEnum, Icon, IconButton, PrimaryButton, SecondaryButton, textHeader } from '@frontend-workspace/react-shared-lib';
import CreateNewCourseDialog from '../create-new-course-dialog/create-new-course-dialog';
import EditCourseDialog from '../edit-course-dialog/edit-course-dialog';
import DeleteCourseDialog from '../delete-course-dialog/delete-course-dialog';
import CopyCourseDialog from '../copy-course-dialog/copy-course-dialog';
import { ICourse, ICourseWithCount } from '../../../_models/course';
import { ICourseRegistrationCheckConflict } from '../../../_models/course-registration-check-conflict';
import { ICourseRegistrationCount } from '../../../_models/course-registration-count';
import RegistrationConflictDialog from '../registration-conflict-dialog/registration-conflict-dialog';
import { numberToNok } from '../../../helpers';
import { useTranslation } from 'react-i18next';


/* eslint-disable-next-line */
export interface CourseGridProps {
  courses: ICourse[],
  refreshData: () => void,
  onDateChange: (dateRange: DateRange<Date> | null) => void,
  dateRange: DateRange<Date>,
  isLoading: boolean
}

export function CourseGrid(props: CourseGridProps) {
  const { data: count }: { data: ICourseRegistrationCount[] } = useCourseGetRequestHook("course/registrations/count", []);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);

  const [isNewCourseOpen, setIsNewCourseOpen] = useState(false);
  const [editCourse, setEditCourse] = useState<ICourse | undefined>(undefined);
  const [copyCourse, setCopyCourse] = useState<ICourse | undefined>(undefined);
  const [deleteCourse, setDeleteCourse] = useState<ICourse | undefined>(undefined);
  const [courseWithCount, setCourseWithCount] = useState<ICourseWithCount[]>([]);
  const [courseConflict, setCourseConflict] = useState<ICourseRegistrationCheckConflict[]>([]);
  const [hideCanceled, setHideCanceled] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>("");
  const [conflictOpen, setConflictOpen] = useState(false);

  const openConflictDialog = (e: string) => {
    setErrorText(e);
    setConflictOpen(true);
  }
  const closeConflictDialog = () => setConflictOpen(false);

  const toggleNewCourseOpen = () => setIsNewCourseOpen(!isNewCourseOpen);

  const openEditCourseDialog = (e: any) => setEditCourse(e.data);
  const closeEditCourseDialog = () => setEditCourse(undefined);

  const openCopyCourseDialog = (e: any) => setCopyCourse(e.data);
  const closeCopyCourseDialog = () => setCopyCourse(undefined);

  const openDeleteCourseDialog = (e: any) => setDeleteCourse(e.data);
  const closeDeleteCourseDialog = () => setDeleteCourse(undefined);

  useEffect(() => {
    if (props.courses.length && count.length) {
      const combineLists = () => {
        const combinedList = props.courses.map((course) => {
          const matchingItemCount = count.find((count) => count.relatedId === course.id);
          const matchingAppointment = courseConflict.find((conflict) => conflict.appointmentId.toString() === course.appointmentId);
          return {
            ...course,
            count: matchingItemCount?.count || 0,
            hasConflict: matchingAppointment?.hasConflict || false,
            tooltips: matchingAppointment?.dates.map(date => date.tooltip).filter(tooltip => tooltip !== null) || []
          };
        });
        setCourseWithCount(combinedList);
      };
      combineLists();
    }
  }, [props.courses, count, courseConflict]);

  const priceRenderer = (params: ValueFormatterParams) => {
    const price = params.value;
    return `${numberToNok(price?.value)}`;
  };

  const editRenderer = (params: ValueFormatterParams) => {
    return (
      <PrimaryButton
        variant="contained"
        size='small'
        onClick={() => openEditCourseDialog(params)}>
        {t("standard.change")}
      </PrimaryButton>
    );
  };

  const copyRenderer = (params: ValueFormatterParams) => {
    return (
      <PrimaryButton
        variant="outlined"
        size='small'
        onClick={() => openCopyCourseDialog(params)}>
        {t("standard.copies")}
      </PrimaryButton>
    );
  };

  const registrationsRenderer = (params: ValueFormatterParams) => {
    const navigateToRegistration = () => navigate(`/course-admin/course/registrasions/${params.data.id}`);
    return (
      <SecondaryButton
        variant="outlined"
        size='small'
        onClick={navigateToRegistration}>
        {t("courseGrid.registrations")}
      </SecondaryButton>
    );
  };

  const deleteRenderer = (params: ValueFormatterParams) => {
    return (
      <ErrorButton
        variant="outlined"
        size='small'
        onClick={() => openDeleteCourseDialog(params)}>
        {t("standard.delete")}
      </ErrorButton>
    );
  };

  const statusRenderer = (params: ValueFormatterParams) => {
    if (params.data.canceled) return (<span style={{ color: '#A30014' }}>{t("standard.cancelled")}</span>);
    if (params.value) {
      return <IconButton onClick={() => openConflictDialog(params.value)}>
        <Icon className="material-icons-outlined" color='error'>info</Icon>
      </IconButton>
    }
    return (<span style={{ color: '#2e7d32' }}>{t("courseGrid.noConflict")}</span>);

  };
  const courses = courseWithCount.filter(course => !hideCanceled || !course.canceled);

  return (<>
    <RightsWrapper>
      <div className="d-flex flex-row align-items-center flex-wrap mb-3 mb-xl-0">
        <div className='mb-3 mb-sm-0 me-3 me-md-4'>
          <PrimaryButton
            variant="contained"
            onClick={toggleNewCourseOpen}>
            <Icon className="me-1" fontSize="small">add</Icon>{t("standard.newCourse")}
          </PrimaryButton>
        </div>
      </div>
    </RightsWrapper>

    <WebkuaTable
      isLoading={props.isLoading}
      data={courses}
      onSetDateRange={props.onDateChange}
      dateRange={props.dateRange}
      supportFutureDates={true}
      tableFilters={[
        {
          label: t("courseGrid.hideCanceled"),
          checked: hideCanceled,
          onChange: () => setHideCanceled(!hideCanceled)
        }
      ]}
      headers={CreateHeaders([
        dateHeader("date", t("standard.date")),
        textHeader("adskCourseId", "ADSK ID", HeaderSizeEnum.MEDIUM),
        textHeader("courseName", t("standard.courseName"), HeaderSizeEnum.EXTRA_LARGE),
        textHeader("trainingRoom.name", t("standard.trainingRoom"), HeaderSizeEnum.MEDIUM),
        textHeader("duration", t("standard.duration"), HeaderSizeEnum.SMALL, { renderer: durationRenderer }),
        textHeader("count", t("standard.quantity"), HeaderSizeEnum.SMALL),
        textHeader("instructor.person.firstName", t("courseGrid.firstNameInstr")),
        textHeader("instructor.person.lastName", t("courseGrid.lastNameInstr")),
        textHeader("calendarEventId", "Calendar ID", HeaderSizeEnum.MEDIUM),
        textHeader("city", t("standard.place"), HeaderSizeEnum.SMALL),
        textHeader("price", t("standard.price"), HeaderSizeEnum.MEDIUM, {
          renderer: priceRenderer,
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        }),
        textHeader("", "", HeaderSizeEnum.SMALL, {
          renderer: editRenderer,
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        }),
        textHeader("", "", HeaderSizeEnum.SMALL, {
          renderer: copyRenderer,
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        }),
        textHeader("", "", HeaderSizeEnum.LARGE, {
          renderer: registrationsRenderer,
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        }),
        textHeader("", "", HeaderSizeEnum.SMALL, {
          renderer: deleteRenderer,
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        }),
        textHeader("conflictText", "Status", HeaderSizeEnum.MEDIUM, {
          renderer: statusRenderer,
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        })
      ])}
    />
    {isNewCourseOpen && <CreateNewCourseDialog
      onClose={toggleNewCourseOpen}
      open={!!isNewCourseOpen}
      refreshData={props.refreshData}
    />}
    {editCourse && editCourse.id && <EditCourseDialog
      onClose={closeEditCourseDialog}
      open={!!editCourse}
      refreshData={props.refreshData}
      courseId={editCourse.id}

    />}
    {copyCourse && copyCourse.id && <CopyCourseDialog
      onClose={closeCopyCourseDialog}
      open={!!copyCourse}
      refreshData={props.refreshData}
      courseId={copyCourse.id}
    />}
    {deleteCourse && deleteCourse.id && <DeleteCourseDialog
      onClose={closeDeleteCourseDialog}
      open={!!deleteCourse}
      refreshData={props.refreshData}
      courseId={deleteCourse.id}
    />}

    {conflictOpen && <RegistrationConflictDialog
      onClose={closeConflictDialog}
      open={!!conflictOpen}
      refreshData={props.refreshData}
      errorText={errorText}
    />}
  </>);
}

export default CourseGrid;
